import type { ItemAuditoriaResumo } from "../../lib/auditoria/tipos";
import { formatarDataHoraISO } from "../../lib/formato/data";

/*
 * Tabela da trilha de auditoria (FE-5 CP-C — LEITURA).
 *
 * Renderiza os `ItemAuditoriaResumo` (array PURO — sem total/envelope). Cada linha
 * é um BOTÃO acessível: clicar seleciona o registro (`aoSelecionar(id)`), o que
 * carrega o detalhe (`antes`/`depois`) num painel ao lado. A linha selecionada é
 * sinalizada por `aria-pressed` + destaque visual.
 *
 * Colunas: ts (formatado `DD/MM/YYYY HH:MM`), ação, recurso, usuário (ou "—") e
 * correlation_id (ou "—"). `usuario_id`/`correlation_id` são anuláveis (eventos de
 * sistema/sem correlação).
 *
 * ESTADO VAZIO: a query resolveu com `[]` (nenhum registro para os filtros) →
 * mensagem clara, não uma tabela vazia.
 */

const SEM_VALOR = "—";

interface TabelaTrilhaProps {
  itens: ItemAuditoriaResumo[];
  /** Id do registro atualmente selecionado (linha destacada), ou `null`. */
  selecionado: string | null;
  /** Notifica a seleção de uma linha (carrega o detalhe). */
  aoSelecionar: (registroId: string) => void;
}

export function TabelaTrilha({
  itens,
  selecionado,
  aoSelecionar,
}: TabelaTrilhaProps): JSX.Element {
  if (itens.length === 0) {
    return (
      <p className="font-corpo text-base text-off-black">
        Nenhum registro na trilha para estes filtros.
      </p>
    );
  }

  return (
    <table className="w-full border-collapse font-corpo text-base text-off-black">
      <thead>
        <tr className="border-b border-platinum text-left">
          <th className="px-2 py-1 font-subtitulo">Data/hora</th>
          <th className="px-2 py-1 font-subtitulo">Ação</th>
          <th className="px-2 py-1 font-subtitulo">Recurso</th>
          <th className="px-2 py-1 font-subtitulo">Usuário</th>
          <th className="px-2 py-1 font-subtitulo">Correlação</th>
        </tr>
      </thead>
      <tbody>
        {itens.map((item) => {
          const ativa = item.id === selecionado;
          return (
            <tr
              key={item.id}
              className={
                ativa
                  ? "border-b border-platinum bg-platinum"
                  : "border-b border-platinum bg-snow"
              }
            >
              <td className="px-2 py-1">
                <button
                  type="button"
                  aria-pressed={ativa}
                  onClick={() => aoSelecionar(item.id)}
                  className="text-left text-off-black hover:text-flame"
                >
                  {formatarDataHoraISO(item.ts)}
                </button>
              </td>
              <td className="px-2 py-1">{item.acao}</td>
              <td className="px-2 py-1">{item.recurso}</td>
              <td className="px-2 py-1">{item.usuario_id ?? SEM_VALOR}</td>
              <td className="px-2 py-1">{item.correlation_id ?? SEM_VALOR}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
